/**
 * Conversational playdate "when" labels.
 *
 * Daypart mode: Morning / Afternoon / Evening — never fabricate clock times.
 * Exact mode: explicit local clock ("Saturday at 2:30 PM").
 */

import type { CirclePlaydatePresentationMode } from '@/lib/play/circlePlaydatePlanMeta'

export type CirclePlaydatePeriod = 'Morning' | 'Afternoon' | 'Evening'

export function periodFromLocalHour(hour: number): CirclePlaydatePeriod {
  if (hour < 12) return 'Morning'
  if (hour < 17) return 'Afternoon'
  return 'Evening'
}

function localParts(iso: string, timeZone: string) {
  const date = new Date(iso)
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
    weekday: 'long',
  }).formatToParts(date)
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? ''
  const hourRaw = get('hour')
  const hour = Number(hourRaw === '24' ? '0' : hourRaw)
  const minute = Number(get('minute') || '0')
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number.isFinite(hour) ? hour : 12,
    minute: Number.isFinite(minute) ? minute : 0,
    weekday: get('weekday'),
  }
}

function dayKey(year: number, month: number, day: number): string {
  return `${year}-${month}-${day}`
}

function formatExactClock(
  iso: string,
  timeZone: string,
  locale?: string
): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso))
}

function dayPrefix(
  target: ReturnType<typeof localParts>,
  nowParts: ReturnType<typeof localParts>,
  scheduledStartAt: string,
  timeZone: string,
  now: Date,
  locale?: string
): string {
  const todayKey = dayKey(nowParts.year, nowParts.month, nowParts.day)
  const targetKey = dayKey(target.year, target.month, target.day)

  if (targetKey === todayKey) return 'Today'

  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000)
  const tomorrowParts = localParts(tomorrow.toISOString(), timeZone)
  if (targetKey === dayKey(tomorrowParts.year, tomorrowParts.month, tomorrowParts.day)) {
    return 'Tomorrow'
  }

  const startOfToday = Date.UTC(nowParts.year, nowParts.month - 1, nowParts.day)
  const startOfTarget = Date.UTC(target.year, target.month - 1, target.day)
  const dayDiff = Math.round((startOfTarget - startOfToday) / (24 * 60 * 60 * 1000))
  if (dayDiff > 1 && dayDiff < 7) {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      weekday: 'long',
    }).format(new Date(scheduledStartAt))
  }

  return new Intl.DateTimeFormat(locale, {
    timeZone,
    month: 'short',
    day: 'numeric',
    ...(target.year !== nowParts.year ? { year: 'numeric' as const } : {}),
  }).format(new Date(scheduledStartAt))
}

/**
 * e.g. daypart: "Tomorrow Morning", "Saturday Afternoon"
 * exact: "Saturday at 2:30 PM"
 */
export function formatCirclePlaydateWhenLabel(
  input: {
    scheduledStartAt: string
    timezone: string
    presentationMode?: CirclePlaydatePresentationMode | null
  },
  now: Date = new Date(),
  locale?: string
): string {
  try {
    const mode = input.presentationMode ?? 'daypart'
    const target = localParts(input.scheduledStartAt, input.timezone)
    const nowParts = localParts(now.toISOString(), input.timezone)
    const prefix = dayPrefix(
      target,
      nowParts,
      input.scheduledStartAt,
      input.timezone,
      now,
      locale
    )

    if (mode === 'exact') {
      const clock = formatExactClock(input.scheduledStartAt, input.timezone, locale)
      return `${prefix} at ${clock}`
    }

    const period = periodFromLocalHour(target.hour)
    return `${prefix} ${period}`
  } catch {
    return 'Upcoming'
  }
}
